import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useToasts } from 'hooks/toasts';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BeneficiariesState } from 'stores/beneficiaries';
import BeneficiariesService, { BeneficiaryView, CounterpartyType, EBeneficiaryMode } from 'services/beneficiaries.service';
import BeneficiarySearchBar from './BeneficiarySearchBar';
import BeneficiaryTable from './BeneficiaryTable';
import BeneficiaryTablePagination from './BeneficiaryTablePagination';
import CounterpartyModal from './BeneficiaryModal';
import BeneficiaryTitularModal from './BeneficiaryTitularModal';

const BeneficiaryList: FC = () => {
  const toast = useToasts();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isTitularModalOpen, setIsTitularModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<BeneficiaryView[]>([]);
  const [limit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const { type } = useSelector<IRootState, BeneficiariesState>(({ beneficiaries }) => beneficiaries);
  const [selected, setSelected] = useState<BeneficiaryView | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const service = useMemo(() => new BeneficiariesService(), []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const counterpartyType: CounterpartyType = type === 'boleto' ? 'BOLETO_ISSUANCE' : 'FUNDS_TRANSFER';

        const mode = type === 'titularidade' ? EBeneficiaryMode.ACCOUNT_HOLDER : EBeneficiaryMode.OTHER;

        const res = await service.list(currentPage, limit, mode, {
          fullSearch: searchTerm || undefined,
          counterpartyType,
        });

        setData(res.items || []);
        const pages = Math.max(1, Math.ceil((res.total || 0) / (res.limit || limit)));
        setTotalPages(pages);
      } catch (err) {
        toast.triggerToast('Erro ao carregar financiadores/beneficiários.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [service, currentPage, limit, searchTerm, type, reloadKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [type]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const displayData = data || [];

  return (
    <Box>
      <BeneficiarySearchBar
        onSearch={handleSearch}
        onCreate={() => {
          if (type === 'titularidade') {
            setIsTitularModalOpen(true);
          } else {
            setIsModalOpen(true);
          }
        }}
      />
      {loading ? (
        <CircularProgress />
      ) : (
        <BeneficiaryTable
          data={displayData}
          onEdit={(id) => {
            const found = (data || []).find((it) => Number(it.id) === Number(id));

            if (found) {
              if (type === 'titularidade') {
                setIsTitularModalOpen(true);
              } else {
                setIsModalOpen(true);
              }

              setSelected(found);
            }
          }}
        />
      )}
      <BeneficiaryTablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />

      <CounterpartyModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelected(undefined);
        }}
        mode={selected ? 'edit' : 'create'}
        initialData={selected ?? undefined}
        onSubmitted={() => {
          setSelected(undefined);
          setIsModalOpen(false);
          setReloadKey((k) => k + 1);
        }}
      />

      <BeneficiaryTitularModal
        open={isTitularModalOpen}
        onClose={() => {
          setIsTitularModalOpen(false);
          setSelected(undefined);
        }}
        mode={selected ? 'edit' : 'create'}
        initialData={selected ?? undefined}
        onSubmitted={() => {
          setSelected(undefined);
          setIsTitularModalOpen(false);
          setReloadKey((k) => k + 1);
        }}
      />
    </Box>
  );
};

export default BeneficiaryList;
